<template>
  <div class="bg-surface-primary p-16">
    <div
      v-if="
        !checkoutStore.isCartEmpty && !fetchPending && fetchStatus !== 'pending'
      "
    >
      <h2 class="w-full flex gap-8 pb-8">
        <span class="heading-xxs text-text-primary">Varukorg</span>
        <span class="body-md text-text-primary">
          ({{ numberOfCartItems }}
          {{ numberOfCartItems == 1 ? 'Produkt' : 'Produkter' }})
        </span>
      </h2>

      <AppAccordion
        type="link"
        :is-open="true"
        :base-html-id="accordionId"
        class="block md:hidden"
        @on-toggle="checkoutStore.ui.cartAccordionIsOpen = $event.isOpen"
      >
        <template #header>
          <p
            class="heading-xxs text-text-link underline hover:text-text-link-hover"
          >
            <span v-if="checkoutStore.ui.cartAccordionIsOpen"
              >Göm innehåll</span
            >
            <span v-else>Visa innehåll</span>
          </p>
        </template>
        <CheckoutCartItemList @on-remove-cart-item="removeCartItem($event)" />
      </AppAccordion>
      <CheckoutCartItemList
        class="hidden md:block"
        @on-remove-cart-item="removeCartItem($event)"
        @on-remove-insurance-item="removeInsuranceItem($event)"
      />
      <div
        class="w-full flex flex-col gap-8 pt-24 border-t-md border-solid border-border-secondary"
      >
        <div
          v-if="checkoutStore.fetchedData.cart.appliedDiscounts.length > 0"
          class="flex flex-col gap-8"
        >
          <template
            v-for="discount in deduplicateDiscounts(
              checkoutStore.fetchedData.cart.appliedDiscounts,
            )"
            :key="discount.Id"
          >
            <CheckoutCartDiscountLabels
              :discount-text-cart="discount.ShortDescriptionShoppingCart"
            />
          </template>
        </div>

        <CheckoutCartPriceTextRow label-text="Månadskostnad">
          {{
            checkoutStore.fetchedData.cart.subTotals?.Payments?.filter(
              (payment) =>
                payment.DownPayments ===
                checkoutStore.userState.payment.selectedDownPayTime,
            )[0]?.MonthlyPayment.toLocaleString('sv-SE')
          }}
          kr/mån
        </CheckoutCartPriceTextRow>
        <div
          v-if="
            !checkoutStore.isKlarnaPaymentMethodSelected &&
            checkoutStore.fetchedData.cart.subTotals?.Payments?.filter(
              (payment) =>
                payment.DownPayments ===
                checkoutStore.userState.payment.selectedDownPayTime,
            )[0]?.MonthlyPaymentWithBuyback !== undefined
          "
        >
          <CheckoutCartPriceTextRow label-text="Månadskostnad med inbyte">
            {{
              checkoutStore.fetchedData.cart.subTotals?.Payments?.filter(
                (payment) =>
                  payment.DownPayments ===
                  checkoutStore.userState.payment.selectedDownPayTime,
              )[0]?.MonthlyPaymentWithBuyback.toLocaleString('sv-SE')
            }}
            kr/mån
          </CheckoutCartPriceTextRow>
        </div>

        <CheckoutCartPriceTextRow :label-text="directPaymentLabel">
          {{
            checkoutStore.fetchedData.cart?.subTotals?.Payments?.filter(
              (payment) =>
                payment.DownPayments ===
                checkoutStore.userState.payment.selectedDownPayTime,
            )[0]?.UpfrontPayment.toLocaleString('sv-SE')
          }}
          kr
        </CheckoutCartPriceTextRow>
      </div>
    </div>
    <div v-if="fetchPending || fetchStatus === 'pending'">
      <RoundedLoadingBox message="Laddar din varukorg" />
    </div>
    <div
      v-else-if="checkoutStore.isCartEmpty"
      class="w-full flex flex-col gap-8 pt-24"
    >
      <p>Det är väldigt tomt i din varukorg!</p>
      <NuxtLink to="/mobiltelefoner" external>Spring till shoppen!</NuxtLink>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { AsyncDataRequestStatus } from '#app';
import type { AppliedDiscount } from '~/models/shopping_cart/ShoppingCart.api.model';
const { $api } = useNuxtApp();
const checkoutStore = useMyCheckoutStore();
const notifications = useNotificationStore();

interface Props {
  deduplicatedAppliedDiscounts: AppliedDiscount[];
  fetchPending: boolean;
  fetchStatus: AsyncDataRequestStatus;
}

defineProps<Props>();

const emit = defineEmits(['on-should-refresh']);

const accordionId = useId();

const directPaymentLabel = computed(() => {
  if (
    checkoutStore.isKlarnaPaymentMethodSelected &&
    !checkoutStore.isKlarnaPartialPaymentMethodSelected
  ) {
    return 'Att betala med Klarna';
  }

  return 'Att betala nu';
});

const deduplicateDiscounts = (discounts?: AppliedDiscount[]) => {
  if (!discounts) {
    return [];
  }

  const seen = new Set();

  return discounts.filter((discount) => {
    if (seen.has(discount.Id)) {
      return false;
    }

    seen.add(discount.Id);
    return true;
  });
};

const removeCartItem = async (id: number) => {
  try {
    await $api.ShoppingCartModule.RemoveShoppingCartItem(id);
    emit('on-should-refresh');
  } catch (error) {
    notifications.setNewNotification({
      type: 'error',
      title: 'Ett fel uppstod',
      msg: 'Ett fel uppstod när Produkten försökte raderas från varukorgen, vänligen försök igen',
      isCloseable: true,
      link: {
        url: 'https://www.halebop.se/support',
        text: 'Om felet kvarstår kontakta supporten',
      },
    });
  }
};

const removeInsuranceItem = async (itemId: number) => {
  await $api.ShoppingCartModule.RemoveInsuranceItem(itemId);
  emit('on-should-refresh');
};

const numberOfCartItems = computed(() => {
  const hasBundlePlanItem = checkoutStore.fetchedData.cart.cartItems.filter(
    (item) => item.BundlePlanItem !== undefined,
  );

  return (
    hasBundlePlanItem.length + checkoutStore.fetchedData.cart.cartItems.length
  );
});
</script>

<style scoped lang="scss"></style>
